import React, { useRef, useEffect, useCallback, useState } from 'react';

import { keyBy } from 'lodash';
import { isRiskOrOpportunityBetter, isRiskOrOpportunityWorse } from 'utils/rules';

import { baseMargin, circlePlacementDx, circlePlacementDy, circleSpacer, textDx, textDy } from '../RiskScatterFullChart.functions';

const isSelected = (pointsList: any[], pointId: string) => {
    if (!pointsList) return false;
    return pointsList.find(p => p.id == pointId);
}

const getAllInitials = (name: string) => {
    const clearedName = name ? name.replace(/[^a-zA-Z0-9]/g, ' ').replace(/(\s)+/, ' ') : '';
    return clearedName.split(' ').slice(0, 2).map((n) => (n[0] ? n[0].toUpperCase() : '') + (n[1] ? n[1] : '')).join('')
}

const PointsCloudInner = (props: any) => {
    // Track previous data IDs to detect newly appearing elements
    const prevDataIdsRef = useRef<Set<string> | null>(null);
    const prevDataRef = useRef<Map<string, any>>(new Map());
    const [appearingIds, setAppearingIds] = useState<Set<string>>(new Set());
    const appearingTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const currentIds = new Set(props.data.map((d: any) => d.id));
        const prevIds = prevDataIdsRef.current;

        // Skip animation on first render - just initialize the ref
        if (prevIds === null) {
            prevDataIdsRef.current = currentIds;
            // Store initial data for potential future disappearing animations
            const dataMap = new Map();
            props.data.forEach((d: any) => dataMap.set(d.id, d));
            prevDataRef.current = dataMap;
            return;
        }

        // Find new IDs that weren't in the previous render
        const newAppearingIds = new Set<string>();
        currentIds.forEach(id => {
            if (!prevIds.has(id)) {
                newAppearingIds.add(id);
            }
        });

        // Update previous IDs ref and data map
        prevDataIdsRef.current = currentIds;
        const dataMap = new Map();
        props.data.forEach((d: any) => dataMap.set(d.id, d));
        prevDataRef.current = dataMap;

        // Handle appearing items - clear previous timer first
        if (appearingTimerRef.current) {
            clearTimeout(appearingTimerRef.current);
        }
        
        if (newAppearingIds.size > 0) {
            setAppearingIds(newAppearingIds);
            appearingTimerRef.current = setTimeout(() => {
                setAppearingIds(new Set());
            }, 1500);
        }

        return () => {
            if (appearingTimerRef.current) {
                clearTimeout(appearingTimerRef.current);
            }
        };
    }, [props.data]);

    const hasSomeElementsSelected = props.isSelectedMode || props.selectedPoints.length > 0

    const { data, isRiskView, riskCategories = [] } = props;

    // Use similarItems from parent - required for proper link coordination
    const similarRisks = props.similarItems;

    const margin = { top: 30, right: 30, bottom: 40, left: 40 },
        width = props.width - margin.left - margin.right

    // Function to calculate point radius based on risk category weight
    const getPointRadius = (risk: any) => {
        const radiuses = [2, 4, 5.5, 7];

        if (!risk.riskCategoryId || !riskCategories.length) {
            return 4; // Default radius
        }

        const category = risk.riskCategory || riskCategories.find((cat: any) => cat.id === risk.riskCategoryId);
        if (!category || !category.weight) {
            return radiuses[1]; // Default radius if no weight
        }

        return radiuses[category.weight];
    };

    const isRecentlyCreated = (id: string): boolean => {
        return props.recentCreations && props.recentCreations[id]
    }

    const isAppearing = (id: string): boolean => {
        return appearingIds.has(id);
    }

    return (

        <g className={`wrapper ${hasSomeElementsSelected ? "selectionMode" : ""}`} >

            {
                props.data.map((d: any, index: number) => {
                    // Skip rendering if element doesn't exist in similarRisks (needed for proper link coordination)
                    if (!similarRisks || !similarRisks[d.id]) return null;

                    const isWorse = isRiskOrOpportunityWorse(d);
                    const isBetter = isRiskOrOpportunityBetter(d);
                    const selected = isSelected(props.selectedPoints, d.id)
                    const radius = getPointRadius(d);
                    const additionalRadius = 4 - radius;

                    // Calculate offset for similar items - same formula as in riskXY/opportunityXY
                    // Must use similarRisks[d.id] to get the enriched object with correct index
                    const similarItem = similarRisks[d.id];
                    // Echelle du decalage de cluster : min(largeur, hauteur) de la zone
                    // -> grappes circulaires, pas d'etirement horizontal quand la zone
                    // est en pleine largeur (Pierre 2026-06-25).
                    const clusterScale = Math.min(props.width, props.height);
                    const offsetX = circleSpacer(similarItem.similarCount) * circlePlacementDx(similarItem, similarRisks) * clusterScale * baseMargin;
                    const offsetY = circleSpacer(similarItem.similarCount) * circlePlacementDy(similarItem, similarRisks) * clusterScale * baseMargin;

                    return <g key={d.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (props.onClick) props.onClick(d)
                        }}
                        onDoubleClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (props.onDoubleClick) props.onDoubleClick(d)
                        }}
                        onMouseOver={() => {
                            if (props.onHover && !props.isDragging) props.onHover(d)
                        }}
                        onMouseOut={() => {
                            if (props.onHover && !props.isDragging) props.onHover(null)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (props.onDragStart) {
                                props.onDragStart(d, e);
                            }
                        }}
                        style={{
                            transform: (() => {
                                // Calculate position: must match exactly the logic in riskXY/opportunityXY
                                let impact = d.impact;
                                let likelihood = d.likelihood;
                                
                                if (props.isDragging && props.draggedItem?.id === d.id) {
                                    impact = props.snapPosition?.impact ?? d.impact;
                                    likelihood = props.snapPosition?.likelihood ?? d.likelihood;
                                } else if (props.showConfirmationModal && props.pendingUpdate?.item?.id === d.id) {
                                    impact = props.frozenPosition?.impact ?? d.impact;
                                    likelihood = props.frozenPosition?.likelihood ?? d.likelihood;
                                }
                                
                                // Include offset in transform to match link position calculations exactly
                                return `translate(${props.x(impact) + offsetX}px, ${props.y(likelihood) + offsetY}px)`;
                            })(),
                            transition: (props.isDragging && props.draggedItem?.id === d.id) ||
                                (props.showConfirmationModal && props.pendingUpdate && props.pendingUpdate.item?.id === d.id)
                                ? "none" : "transform 1.8s ease-in-out",
                            cursor: props.isDragging ? "grabbing" : "grab"
                        }}
                        className={`stakeholder ${selected ? "selected" : ""} ${isRecentlyCreated(d.id) ? "recentlyCreated" : ""} ${isAppearing(d.id) ? "appearing" : ""} ${props.isDragging && props.draggedItem?.id === d.id ? "dragging" : ""}`}
                    >
                        <foreignObject className="aura" width={200} height={200}>
                            <div></div>
                        </foreignObject>
                        <circle
                            className="risk-circle"
                            cx={0}
                            cy={0}
                            r={getPointRadius(d)}
                            fill={isSelected(props.selectedPoints, d.id) ? "orange" : isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" :
                                    "#000000"
                            }
                            strokeWidth={isSelected(props.selectedPoints, d.id) ? "4" : isWorse ? "6" :
                                isBetter ? "6" :
                                    "6"
                            }
                            stroke={isSelected(props.selectedPoints, d.id) ? "orange" : isWorse && d.isOpportunity ? "#00000044" : isWorse && !d.isOpportunity ? "#FFFFFF55" :
                                isBetter && d.isOpportunity ? "#FFFFFF55" : isBetter && !d.isOpportunity ? "#00000044" :
                                    "#00000001"
                            }
                            style={{
                                transition: isSelected(props.selectedPoints, d.id) ? "stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out" : "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                        >

                        </circle>

                        <circle
                            className="risk-circle"
                            cx={0}
                            cy={0}
                            r={getPointRadius(d)}
                            fill={props.selectedPoint && props.selectedPoint.id == d.id ? "orange" : isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" :
                                    "#000000"
                            }
                            strokeWidth={isWorse ? "1" :
                                isBetter ? "1" :
                                    "6"
                            }
                            stroke={isWorse ? "#FFFFFF" :
                                isBetter ? "#FFFFFF" :
                                    "#00000001"
                            }
                            style={{
                                transition: isSelected(props.selectedPoints, d.id) ? "stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out" : "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                        >

                        </circle>


                        <text
                            className="risk-label"
                            x={0}
                            y={0}
                            dx={textDx(similarItem, similarRisks, clusterScale, baseMargin) - offsetX}
                            dy={textDy(similarItem, similarRisks, clusterScale, baseMargin) + additionalRadius - offsetY}
                            fill={isWorse ? "#E70549" :
                                isBetter ? "#24CCB8" : "#00000077"}
                            strokeWidth={isWorse ? "#FFFFFF" :
                                isBetter ? "#FFFFFF" :
                                    "6"
                            }
                            stroke={isWorse ? "#FFFFFF55" :
                                isBetter ? "#FFFFFF55" :
                                    "#00000001"
                            }
                            style={{
                                fontSize: "12px",
                                textAnchor: Math.abs(circlePlacementDx(similarItem, similarRisks) * 10) < 0.1 ? "middle" :
                                    circlePlacementDx(similarItem, similarRisks) < 0 ? "end" :
                                        circlePlacementDx(similarItem, similarRisks) > 0 ? "start" : "middle",
                                alignmentBaseline: "middle",
                                transition: "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                            transform='translate(0, -10)'

                        >
                            {d.reference || getAllInitials(d.name)}
                            <circle
                                r={2.5}
                                fill="#000000"
                            ></circle>
                        </text>

                        <text
                            className="risk-label"
                            x={0}
                            y={0}
                            dx={textDx(similarItem, similarRisks, clusterScale, baseMargin) - offsetX}
                            dy={textDy(similarItem, similarRisks, clusterScale, baseMargin) + additionalRadius - offsetY}
                            fill={"#000000"}
                            style={{
                                fontSize: "12px",
                                textAnchor: Math.abs(circlePlacementDx(similarItem, similarRisks) * 10) < 0.1 ? "middle" :
                                    circlePlacementDx(similarItem, similarRisks) < 0 ? "end" :
                                        circlePlacementDx(similarItem, similarRisks) > 0 ? "start" : "middle",
                                alignmentBaseline: "middle",
                                transition: "fill 1.8s ease-in-out, stroke-width 1.8s ease-in-out, stroke 1.8s ease-in-out"
                            }}
                            transform='translate(0, -10)'

                        >
                            {d.reference || getAllInitials(d.name)}
                            <circle
                                r={2.5}
                                fill="#000000"
                            ></circle>
                        </text>



                    </g>

                })
            }
        </g >





    );
};

// React.memo (cf. StakeholdersPointsCloud) — ignore les callbacks + isDragging
// est inclus parce qu il bloque les events onHover.
const propsEqual = (prev: any, next: any) => {
    return (
        prev.data === next.data &&
        prev.similarItems === next.similarItems &&
        prev.selectedPoints === next.selectedPoints &&
        prev.isSelectedMode === next.isSelectedMode &&
        prev.isDragging === next.isDragging &&
        prev.draggedItem === next.draggedItem &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.scenario === next.scenario &&
        prev.recentCreations === next.recentCreations &&
        prev.margin === next.margin &&
        prev.x === next.x &&
        prev.y === next.y &&
        prev.isOpportunity === next.isOpportunity
    );
};

export const PointsCloud = React.memo(PointsCloudInner, propsEqual);
